import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from 'react-resizable-panels';
import { useCallback, useEffect, useState } from 'react';
import {
  PANEL_LABELS,
  type DockColumnLayout,
  type DockSide,
  type EditorPanelId,
} from '../../types/editorLayout';
import { useEditorLayoutStore } from '../../store/editorLayoutStore';
import { useSimulationStore } from '../../store/simulationStore';
import { renderEditorPanelContent } from './editorPanelContents';
import SimulationCanvas from './SimulationCanvas';
import BottomPanel from './BottomPanel';

// ------------------------------------------------------------
// Dock layout: draggable panels, resizable columns, persistent
// ------------------------------------------------------------

function GripIcon() {
  return (
    <svg
      className="h-3.5 w-3.5 shrink-0 text-text-subtle"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden
    >
      <circle cx="5" cy="4" r="1.2" />
      <circle cx="11" cy="4" r="1.2" />
      <circle cx="5" cy="8" r="1.2" />
      <circle cx="11" cy="8" r="1.2" />
      <circle cx="5" cy="12" r="1.2" />
      <circle cx="11" cy="12" r="1.2" />
    </svg>
  );
}

function ColumnResizeHandle() {
  return (
    <PanelResizeHandle className="group flex w-1.5 shrink-0 items-stretch bg-border/40 transition hover:bg-accent/30">
      <div className="m-auto h-8 w-0.5 rounded-full bg-border group-hover:bg-accent/60" />
    </PanelResizeHandle>
  );
}

function PanelStackResizeHandle({
  side,
  columnId,
  insertIndex,
}: {
  side: DockSide;
  columnId: string;
  insertIndex: number;
}) {
  const movePanel = useEditorLayoutStore((s) => s.movePanel);
  const [dragOver, setDragOver] = useState(false);

  return (
    <PanelResizeHandle
      className={`group flex h-2 shrink-0 items-center transition ${
        dragOver ? 'bg-accent/25' : 'bg-border/40 hover:bg-accent/30'
      }`}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes('application/x-editor-panel')) return;
        e.preventDefault();
        e.stopPropagation();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        const raw = e.dataTransfer.getData('application/x-editor-panel');
        if (!raw) return;
        try {
          const payload = JSON.parse(raw) as { panelId: EditorPanelId };
          movePanel(payload.panelId, side, columnId, insertIndex);
        } catch {
          /* ignore */
        }
      }}
    >
      <div
        className={`mx-auto rounded-full transition ${
          dragOver
            ? 'h-1 w-16 bg-accent'
            : 'h-0.5 w-12 bg-border group-hover:bg-accent/60'
        }`}
      />
    </PanelResizeHandle>
  );
}

function RowResizeHandle() {
  return (
    <PanelResizeHandle className="group flex h-1.5 shrink-0 items-center bg-border/40 transition hover:bg-accent/30">
      <div className="mx-auto h-0.5 w-12 rounded-full bg-border group-hover:bg-accent/60" />
    </PanelResizeHandle>
  );
}

function CloseIcon() {
  return (
    <svg
      className="h-3.5 w-3.5"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden
    >
      <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
    </svg>
  );
}

interface DockPanelChromeProps {
  panelId: EditorPanelId;
  side: DockSide;
  columnId: string;
  index: number;
  children: React.ReactNode;
}

function DockPanelChrome({
  panelId,
  side,
  columnId,
  index,
  children,
}: DockPanelChromeProps) {
  const movePanel = useEditorLayoutStore((s) => s.movePanel);
  const closePanel = useEditorLayoutStore((s) => s.closePanel);
  const [dragOver, setDragOver] = useState<'top' | 'bottom' | null>(null);

  const onDragStart = (e: React.DragEvent) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(
      'application/x-editor-panel',
      JSON.stringify({ panelId, side, columnId, index }),
    );
  };

  const resolveDropPosition = (e: React.DragEvent): 'top' | 'bottom' => {
    const rect = e.currentTarget.getBoundingClientRect();
    const relativeY = e.clientY - rect.top;
    return relativeY < rect.height / 2 ? 'top' : 'bottom';
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(resolveDropPosition(e));
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const position = resolveDropPosition(e);
    setDragOver(null);
    const raw = e.dataTransfer.getData('application/x-editor-panel');
    if (!raw) return;
    try {
      const payload = JSON.parse(raw) as {
        panelId: EditorPanelId;
        side: DockSide;
        columnId: string;
        index: number;
      };
      const targetIndex = position === 'top' ? index : index + 1;
      if (
        payload.panelId === panelId &&
        payload.columnId === columnId &&
        (payload.index === index ||
          payload.index + 1 === targetIndex ||
          payload.index === targetIndex)
      ) {
        return;
      }
      movePanel(payload.panelId, side, columnId, targetIndex);
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      className={`relative flex h-full min-h-0 flex-col overflow-hidden border-b border-border-subtle bg-surface-raised last:border-b-0 ${
        dragOver ? 'ring-1 ring-inset ring-accent/30' : ''
      }`}
      onDragOver={onDragOver}
      onDragLeave={() => setDragOver(null)}
      onDrop={onDrop}
    >
      {dragOver === 'top' && (
        <div
          className="pointer-events-none absolute inset-x-0 top-0 z-10 h-1 bg-accent shadow-[0_0_8px_rgba(220,38,38,0.45)]"
          aria-hidden
        />
      )}
      <header
        draggable
        onDragStart={onDragStart}
        onDragEnd={() => setDragOver(null)}
        className="flex shrink-0 cursor-grab items-center gap-2 border-b border-border-subtle px-2.5 py-2 active:cursor-grabbing"
        title="Drag to move or restack panel"
      >
        <GripIcon />
        <h2 className="panel-section-title min-w-0 flex-1 truncate text-xs">
          {PANEL_LABELS[panelId]}
        </h2>
        <button
          type="button"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            closePanel(panelId);
          }}
          className="shrink-0 rounded p-0.5 text-text-subtle hover:bg-surface-overlay hover:text-text"
          title="Close panel"
          aria-label={`Close ${PANEL_LABELS[panelId]}`}
        >
          <CloseIcon />
        </button>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      {dragOver === 'bottom' && (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-1 bg-accent shadow-[0_0_8px_rgba(220,38,38,0.45)]"
          aria-hidden
        />
      )}
    </div>
  );
}

interface DockColumnProps {
  column: DockColumnLayout;
  side: DockSide;
  defaultSize: number;
}

function DockColumn({ column, side, defaultSize }: DockColumnProps) {
  const moveColumn = useEditorLayoutStore((s) => s.moveColumn);
  const movePanel = useEditorLayoutStore((s) => s.movePanel);
  const updateColumnPanels = useEditorLayoutStore((s) => s.updateColumnPanels);
  const otherSide: DockSide = side === 'left' ? 'right' : 'left';
  const [columnDragOver, setColumnDragOver] = useState(false);

  const onColumnDragStart = (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(
      'application/x-editor-column',
      JSON.stringify({ columnId: column.id, side }),
    );
  };

  const onColumnDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setColumnDragOver(false);
    const colRaw = e.dataTransfer.getData('application/x-editor-column');
    if (colRaw) {
      try {
        const payload = JSON.parse(colRaw) as {
          columnId: string;
          side: DockSide;
        };
        if (payload.side !== side) {
          moveColumn(payload.columnId, payload.side, side);
        }
      } catch {
        /* ignore */
      }
      return;
    }
    const panelRaw = e.dataTransfer.getData('application/x-editor-panel');
    if (panelRaw) {
      try {
        const payload = JSON.parse(panelRaw) as { panelId: EditorPanelId };
        const rect = e.currentTarget.getBoundingClientRect();
        const relativeY = e.clientY - rect.top;
        const slotCount = column.panels.length + 1;
        const insertAt = Math.min(
          column.panels.length,
          Math.max(
            0,
            Math.floor((relativeY / Math.max(rect.height, 1)) * slotCount),
          ),
        );
        movePanel(payload.panelId, side, column.id, insertAt);
      } catch {
        /* ignore */
      }
    }
  };

  return (
    <Panel
      id={`${side}-col-${column.id}`}
      defaultSize={defaultSize}
      minSize={12}
      className="min-w-0"
    >
      <div className="flex h-full min-h-0 flex-col">
        <header
          draggable
          onDragStart={onColumnDragStart}
          className="flex shrink-0 items-center gap-2 border-b border-border bg-surface-overlay/60 px-2 py-1.5"
          title="Drag column to the other side"
        >
          <GripIcon />
          <span className="text-[10px] font-medium uppercase tracking-wide text-text-subtle">
            Column
          </span>
          <button
            type="button"
            className="ml-auto rounded px-1.5 py-0.5 text-[10px] text-text-muted hover:bg-surface-overlay hover:text-text"
            onClick={() => moveColumn(column.id, side, otherSide)}
            title={`Move column to ${otherSide}`}
          >
            {side === 'left' ? '→ Right' : '← Left'}
          </button>
        </header>
        <div
          className={`min-h-0 flex-1 ${columnDragOver ? 'ring-2 ring-inset ring-accent/40' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setColumnDragOver(true);
          }}
          onDragLeave={() => setColumnDragOver(false)}
          onDrop={onColumnDrop}
        >
          <PanelGroup
            direction="vertical"
            onLayout={(sizes) => updateColumnPanels(side, column.id, sizes)}
          >
            {column.panels.flatMap((panelId, index) => {
              const nodes = [
                <Panel
                  key={panelId}
                  id={`${side}-${column.id}-${panelId}`}
                  defaultSize={
                    column.panelSizes[index] ?? 100 / column.panels.length
                  }
                  minSize={8}
                >
                  <DockPanelChrome
                    panelId={panelId}
                    side={side}
                    columnId={column.id}
                    index={index}
                  >
                    {renderEditorPanelContent(panelId)}
                  </DockPanelChrome>
                </Panel>,
              ];
              if (index < column.panels.length - 1) {
                nodes.push(
                  <PanelStackResizeHandle
                    key={`${panelId}-handle`}
                    side={side}
                    columnId={column.id}
                    insertIndex={index + 1}
                  />,
                );
              }
              return nodes;
            })}
          </PanelGroup>
        </div>
      </div>
    </Panel>
  );
}

function DockSideArea({ side }: { side: DockSide }) {
  const layout = useEditorLayoutStore((s) => s.layout);
  const movePanel = useEditorLayoutStore((s) => s.movePanel);
  const updateSideColumns = useEditorLayoutStore((s) => s.updateSideColumns);
  const sideLayout = layout[side];
  const [edgeDragOver, setEdgeDragOver] = useState(false);

  const onEdgeDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setEdgeDragOver(false);
    const raw = e.dataTransfer.getData('application/x-editor-panel');
    if (!raw) return;
    try {
      const payload = JSON.parse(raw) as { panelId: EditorPanelId };
      movePanel(payload.panelId, side, null, 0);
    } catch {
      /* ignore */
    }
  };

  if (sideLayout.columns.length === 0) {
    return (
      <div
        className={`flex h-full items-center justify-center border border-dashed p-4 text-center text-xs text-text-subtle ${
          edgeDragOver ? 'border-accent bg-accent/5' : 'border-border'
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setEdgeDragOver(true);
        }}
        onDragLeave={() => setEdgeDragOver(false)}
        onDrop={onEdgeDrop}
      >
        Drop panels here
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        className={`min-h-0 flex-1 ${edgeDragOver ? 'ring-2 ring-inset ring-accent/30' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setEdgeDragOver(true);
        }}
        onDragLeave={() => setEdgeDragOver(false)}
        onDrop={onEdgeDrop}
      >
        <PanelGroup
          direction="horizontal"
          onLayout={(sizes) => updateSideColumns(side, sizes)}
        >
          {sideLayout.columns.flatMap((column, index) => {
            const nodes = [
              <DockColumn
                key={column.id}
                column={column}
                side={side}
                defaultSize={
                  sideLayout.columnSizes[index] ??
                  100 / sideLayout.columns.length
                }
              />,
            ];
            if (index < sideLayout.columns.length - 1) {
              nodes.push(
                <ColumnResizeHandle key={`${column.id}-handle`} />,
              );
            }
            return nodes;
          })}
        </PanelGroup>
      </div>
    </div>
  );
}

export default function EditorDockLayout() {
  const layout = useEditorLayoutStore((s) => s.layout);
  const hydrated = useEditorLayoutStore((s) => s.hydrated);
  const hydrate = useEditorLayoutStore((s) => s.hydrate);
  const setRootSizes = useEditorLayoutStore((s) => s.setRootSizes);
  const setCenterSizes = useEditorLayoutStore((s) => s.setCenterSizes);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  useEffect(() => {
    return useSimulationStore.subscribe((state, prev) => {
      if (state.bottomPanelOpen !== prev.bottomPanelOpen) {
        useEditorLayoutStore.getState().setBottomOpen(state.bottomPanelOpen);
      }
      if (state.bottomPanelTab !== prev.bottomPanelTab) {
        useEditorLayoutStore.getState().setBottomTab(state.bottomPanelTab);
      }
    });
  }, []);

  const onRootLayout = useCallback(
    (sizes: number[]) => setRootSizes(sizes),
    [setRootSizes],
  );

  const onCenterLayout = useCallback(
    (sizes: number[]) => setCenterSizes(sizes),
    [setCenterSizes],
  );

  if (!hydrated) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-text-muted">
        Loading workspace…
      </div>
    );
  }

  const [leftSize, centerSize, rightSize] = layout.rootSizes;
  const [canvasSize, bottomSize] = layout.centerSizes;

  return (
    <PanelGroup
      direction="horizontal"
      className="min-h-0 flex-1"
      onLayout={onRootLayout}
    >
      <Panel
        id="dock-left"
        defaultSize={leftSize}
        minSize={10}
        maxSize={40}
        className="min-w-0 bg-surface-raised"
      >
        <DockSideArea side="left" />
      </Panel>

      <PanelResizeHandle className="group w-1.5 bg-border/50 transition hover:bg-accent/30" />

      <Panel id="center" defaultSize={centerSize} minSize={35} className="min-w-0">
        <PanelGroup
          direction="vertical"
          className="h-full min-h-0"
          onLayout={onCenterLayout}
        >
          <Panel
            id="canvas"
            defaultSize={layout.bottomOpen ? canvasSize : 100}
            minSize={30}
            className="min-h-0"
          >
            <div className="flex h-full min-h-0 flex-col overflow-hidden">
              <div className="relative min-h-0 flex-1 overflow-hidden">
                <SimulationCanvas />
              </div>
              {!layout.bottomOpen && <BottomPanel />}
            </div>
          </Panel>
          {layout.bottomOpen && (
            <>
              <RowResizeHandle />
              <Panel
                id="bottom"
                defaultSize={bottomSize}
                minSize={12}
                maxSize={60}
                className="min-h-0"
              >
                <BottomPanel docked />
              </Panel>
            </>
          )}
        </PanelGroup>
      </Panel>

      <PanelResizeHandle className="group w-1.5 bg-border/50 transition hover:bg-accent/30" />

      <Panel
        id="dock-right"
        defaultSize={rightSize}
        minSize={10}
        maxSize={45}
        className="min-w-0 bg-surface-raised"
      >
        <DockSideArea side="right" />
      </Panel>
    </PanelGroup>
  );
}
